import { useEffect, useMemo } from 'react';
import { useJnx } from './jnx';
import { getObject } from './mapObject';
import getPathFromId from './getPathFromId';
import { useSideChannelSubscription } from './useSideChannel';

/** Determines whether a field is currently disabled or read-only, combining every convention
 *  used across field widgets (static schema.readOnly / ui:readonly / ui:disabled, plus dynamic
 *  ui:disableIf / ui:readonlyIf expressions), and reports the result into
 *  formContext.disabledFields.current so non-rendering consumers (e.g. validation) can read it.
 */
export default function useIsDisabled({ id, schema = {}, uiSchema = {}, disabled, readonly, formContext }) {
  const path = useMemo(() => getPathFromId(id), [id]);
  const rootFormData = useSideChannelSubscription(formContext?.sideChannel, 0);

  const { 'ui:disableIf': disableIfExpr, 'ui:readonlyIf': readonlyIfExpr, 'ui:readonly': uiReadonly, 'ui:disabled': uiDisabled } = uiSchema;

  const disableIfJnx = useJnx(disableIfExpr);
  const readonlyIfJnx = useJnx(readonlyIfExpr);

  const isDisabled = useMemo(() => {
    if (disabled || readonly || uiReadonly === true || uiDisabled === true || schema.readOnly === true) {
      return true;
    }

    const bindings = { value: getObject(rootFormData, path), rootFormData };

    if (disableIfJnx?.eval(rootFormData, path, bindings)) return true;
    if (readonlyIfJnx?.eval(rootFormData, path, bindings)) return true;

    return false;
  }, [disabled, readonly, uiReadonly, uiDisabled, schema.readOnly, disableIfJnx, readonlyIfJnx, rootFormData, path]);

  useEffect(() => {
    const disabledFields = formContext?.disabledFields?.current;

    if (disabledFields && path) {
      disabledFields[path] = isDisabled;

      return () => {
        // delete disabledFields[path];
      };
    }

    return undefined;
  }, [formContext, path, isDisabled]);

  return isDisabled;
}
